import { useEffect, useRef } from "react";
import { api } from "@/lib/api";

const PING_INTERVAL_MS = 60_000; // 60 s heartbeat — server TTL is 90 s, so a
                                 // tab that goes offline drops in 60–90 s.
const ANON_ID_KEY = "sv_anon_client_id"; // localStorage fallback for never-
                                         // logged-in, never-gated visitors

// Stable per-browser identifier. Order of preference:
//   1. logged-in user.id (richest signal — we can show "1 trader online")
//   2. gate visitor_id (from HomepageGate verification)
//   3. anonymous UUID seeded once into localStorage
// We never send PII (phone, email) — just an opaque id.
function resolveClientId(user) {
  if (user?.id) return { id: `u:${user.id}`, kind: user.role === "admin" ? "admin" : "trader" };
  try {
    const gateId = localStorage.getItem("sv_gate_visitor_id");
    if (gateId) return { id: `g:${gateId}`, kind: "gate_visitor" };
  } catch { /* private mode */ }
  try {
    let anon = localStorage.getItem(ANON_ID_KEY);
    if (!anon) {
      anon = "a:" + (crypto?.randomUUID?.() ||
                     Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem(ANON_ID_KEY, anon);
    }
    return { id: anon, kind: "anon" };
  } catch {
    return { id: "a:noref-" + Date.now().toString(36), kind: "anon" };
  }
}

/**
 * Sends a lightweight presence heartbeat every 30 s while the tab is visible.
 * Skips when hidden so a background tab doesn't inflate the "live users"
 * count (Chrome on mobile keeps tabs alive for ~5 min after switch-away).
 *
 * On `pagehide` we fire a `sendBeacon` so the row drops within ~1 s instead
 * of waiting for the 90 s TTL — keeps the admin's count honest when traders
 * close the tab quickly.
 */
export function usePresenceHeartbeat(user) {
  const lastIdRef = useRef(null);

  useEffect(() => {
    const { id, kind } = resolveClientId(user);
    lastIdRef.current = id;

    const ping = () => {
      if (document.visibilityState !== "visible") return;
      // Strip query/hash so the admin breakdown doesn't get polluted by
      // tracking params; keep just the pathname.
      const page = window.location?.pathname || "/";
      api.post("/presence/ping", { client_id: id, kind, page }).catch(() => {
        /* offline / origin 5xx — try again next interval */
      });
    };

    ping(); // fire immediately on mount so the admin sees us right away
    const tick = window.setInterval(ping, PING_INTERVAL_MS);

    // Update-on-resume: when the tab becomes visible after being hidden,
    // ping immediately instead of waiting up to 30 s for the next interval.
    const onVisibility = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVisibility);

    // Clean leave on tab close. `sendBeacon` is the only way to reliably
    // make a request fire during `pagehide` — fetch/axios won't survive.
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const leave = () => {
      if (!apiBase || !lastIdRef.current) return;
      try {
        // DELETE via beacon — we encode the client_id in the URL.
        const url = `${apiBase}/api/presence/ping?client_id=${encodeURIComponent(lastIdRef.current)}`;
        // Beacon only supports POST, so we send a sentinel body and let the
        // server route DELETE via X-HTTP-Method-Override. Falls back to a
        // best-effort fetch with keepalive if Beacon isn't available.
        if (navigator.sendBeacon) {
          // We can't directly DELETE via Beacon — so we just hit the upsert
          // endpoint with an explicit "leaving" sentinel kind, OR fall back
          // to a keepalive fetch.
          fetch(url, { method: "DELETE", keepalive: true }).catch(() => {});
        } else {
          fetch(url, { method: "DELETE", keepalive: true }).catch(() => {});
        }
      } catch { /* swallowed */ }
    };
    window.addEventListener("pagehide", leave);

    return () => {
      window.clearInterval(tick);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", leave);
    };
  }, [user]);
}
